/**
 * <varga-grid> — the sixteen ṣoḍaśavarga charts as a navigable set, with a note
 * on what each is classically read for.
 *
 * ⚠ D-60 is 0°30′ wide. Wherever it is shown, the birth-time sensitivity note
 * must be visible — that is the whole subject of Phase 13.
 *
 * © Manish Jagdish Thatte
 */

import { SHODASHA_VARGAS } from '../jyotish/varga.js';
import { RASHIS } from '../jyotish/constants.js';
import { localName } from '../utils/i18n.js';

export class VargaGrid extends HTMLElement {
  connectedCallback() {
    if (this._built) return;
    this._built = true;
    this._charts = null;
    this._active = 9; // navāṁśa first — the one people look for
    this.render();
  }

  /** @param {ReturnType<import('../jyotish/varga.js').allVargaCharts> | null} charts */
  set charts(charts) {
    this._charts = charts;
    this.render();
  }
  get charts() { return this._charts; }

  render() {
    this.textContent = '';
    if (!this._charts?.length) {
      const p = document.createElement('p');
      p.className = 'field-note';
      p.textContent = 'Compute a kundali with a birth time to see the sixteen vargas.';
      this.append(p);
      return;
    }

    const tabs = document.createElement('div');
    tabs.className = 'tabs varga-grid__tabs';
    tabs.setAttribute('role', 'tablist');
    for (const c of this._charts) {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'tab';
      b.textContent = `D-${c.d}`;
      b.title = c.meta.name;
      b.setAttribute('aria-selected', String(c.d === this._active));
      b.addEventListener('click', () => { this._active = c.d; this.render(); });
      tabs.append(b);
    }
    this.append(tabs);

    const chart = this._charts.find((c) => c.d === this._active) ?? this._charts[0];
    const panel = document.createElement('div');
    panel.className = 'panel varga-grid__panel';

    const h = document.createElement('h2');
    h.textContent = `D-${chart.d} · ${chart.meta.name}`;
    panel.append(h);

    const reads = document.createElement('p');
    reads.className = 'field-note';
    reads.textContent = `Classically read for: ${chart.meta.reads}.`;
    panel.append(reads);

    if (chart.birthTimeSensitive) {
      const warn = document.createElement('div');
      warn.className = 'notice notice--warning';
      warn.textContent =
        'D-60 divisions are 0°30′ wide. A birth time wrong by a few seconds can ' +
        'change the ṣaṣṭyāṁśa. Treat this chart as provisional unless the time is solid.';
      panel.append(warn);
    }

    if (chart.lagna) {
      const lagna = document.createElement('p');
      lagna.textContent =
        `Varga lagna: ${localName(RASHIS[chart.lagna.rashiIndex].name)}`;
      panel.append(lagna);
    }

    const table = document.createElement('table');
    table.className = 'data-table';
    table.innerHTML = '<thead><tr><th>Graha</th><th>Rāśi</th><th>Dignity</th></tr></thead>';
    const tbody = document.createElement('tbody');
    for (const g of Object.values(chart.grahas)) {
      const tr = document.createElement('tr');
      const r = RASHIS[g.rashiIndex];
      tr.innerHTML =
        `<td>${g.id}</td>` +
        `<td>${r.name.en}</td>` +
        `<td>${g.dignity?.dignity ?? '—'}</td>`;
      tbody.append(tr);
    }
    table.append(tbody);
    panel.append(table);

    // Occupancy by bhāva
    if (chart.lagna) {
      const occ = document.createElement('p');
      occ.className = 'field-note';
      const bits = chart.bhavas.map((ids, i) =>
        ids.length ? `bhāva ${i + 1}: ${ids.join(', ')}` : null
      ).filter(Boolean);
      occ.textContent = bits.join(' · ') || 'No grahas placed.';
      panel.append(occ);
    }

    this.append(panel);
  }
}

customElements.define('varga-grid', VargaGrid);

// Re-export for pages that want the catalogue without importing jyotish directly.
export { SHODASHA_VARGAS };
